
import React, { useRef, KeyboardEvent } from 'react';
import { Search, Plus, Loader2 } from 'lucide-react';
import { MockGooglePlace } from '@/utils/mockGoogleMapsApi';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useBusinessSearch } from '@/hooks/useBusinessSearch';
import SearchDropdownAlert from './SearchDropdownAlert';
import SearchResultsList from './SearchResultsList';

interface BusinessSearchFieldProps {
  onBusinessSelect: (business: MockGooglePlace) => void;
  onCreateNew: (businessName: string) => void;
  initialValue?: string;
  className?: string;
}

const BusinessSearchField: React.FC<BusinessSearchFieldProps> = ({
  onBusinessSelect,
  onCreateNew,
  initialValue = '',
  className
}) => {
  const {
    searchTerm,
    searchResults,
    isDropdownOpen,
    isSearching,
    isLoading,
    usingMockData,
    error,
    hasApiKey,
    handleSearchChange,
    toggleDropdown,
    setIsDropdownOpen
  } = useBusinessSearch(initialValue);
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [setIsDropdownOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handleSearchChange(e.target.value);
  };

  const handleSelectBusiness = (business: MockGooglePlace) => {
    setIsDropdownOpen(false);
    onBusinessSelect(business);
  };

  // Updated to explicitly pass the current searchTerm
  const handleCreateNewClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (searchTerm.trim().length > 0) {
      console.log("Create New clicked with business name:", searchTerm.trim());
      setIsDropdownOpen(false);
      // Explicitly call the provided callback with the current search term
      onCreateNew(searchTerm.trim());
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (searchTerm.trim().length >= 2) {
        console.log("Enter key pressed - creating new business with name:", searchTerm.trim());
        // Directly call the create new function with the current search term
        onCreateNew(searchTerm.trim());
      }
    }
  };

  const showCorsError = error?.includes('CORS proxy access denied');

  return (
    <div 
      className={cn(
        "relative w-full", 
        isMobile && "px-2.5", 
        className
      )} 
      ref={dropdownRef}
    >
      <div className="relative">
        <Search 
          size={16} 
          className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
        />
        <input 
          ref={inputRef}
          type="text" 
          value={searchTerm}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder="Search for a business" 
          className="pl-10 pr-24 py-2 w-full rounded-md border border-gray-200 text-sm focus:outline-none focus:ring-1 focus:ring-brand-green focus:border-brand-green transition-all"
          onClick={() => searchTerm.length >= 2 && toggleDropdown(true)}
        />
        <Button
          size="sm"
          className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 text-xs flex items-center gap-1"
          onClick={handleCreateNewClick}
          disabled={searchTerm.trim().length < 2 || isLoading || isSearching}
          type="button"
        >
          {(isLoading || isSearching) ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Plus size={14} />
          )}
          Create New
        </Button>
      </div>
      
      {isDropdownOpen && (
        <div className={`absolute ${isMobile ? "z-[9999]" : "z-[100]"} mt-1 w-full bg-white shadow-lg rounded-md border border-gray-200 max-h-[300px] overflow-y-auto`}>
          {!hasApiKey && <SearchDropdownAlert type="no-api-key" />}
          
          {showCorsError && <SearchDropdownAlert type="cors-error" />}
          
          {usingMockData && hasApiKey && <SearchDropdownAlert type="mock-data" />}
          
          {error && !showCorsError && !usingMockData ? (
            <SearchDropdownAlert type="error" errorMessage={error} />
          ) : (
            <SearchResultsList
              searchResults={searchResults}
              isLoading={isLoading}
              isSearching={isSearching}
              usingMockData={usingMockData}
              onSelectBusiness={handleSelectBusiness}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default BusinessSearchField;
